import Vue from "vue";
import {
    ExpenseAttachmentLinkAdd,
    ExpenseAttachmentParams,
    ExpenseDictionary,
    ExpenseEditParams,
    ExpenseEligibility,
    ExpenseEligibilityGroup,
    ExpenseEligibilityStatus,
    ExpenseEvidenceUpdateParams,
    ExpenseGroup,
    ExpenseReimburseMeParams,
    ExpenseRequest,
    ExpenseStateNew,
    FetchState,
    ImportedExpense,
    PossibleExpense,
    TransactionToRemove,
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as expenseHelper from "@/employee/expenseHelper";
import * as expenseService from "@/shared/api/expenseService";
import { logEmployeeExpenseAttachmentUploaded } from "@/shared/api/userService";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";
import { useClaimStore } from "@/employee/store/claim";
import { useExpenseBalanceSummaryStore } from "@/employee/store/expenseBalanceSummary";
import { useExpenseUnreimbursedStore } from "@/employee/store/expenseUnreimbursed";
import { useExpenseReimbursedStore } from "@/employee/store/expenseReimbursed";

declare function parseInt (s: string | number, radix?: number): number;

const pageSize = 10;

export const initialState: ExpenseStateNew = {
    expenses: {
        records: {},
        page: 1,
        numberOfPages: 0,
        transactionType: "expenses",
        expenseFetchState: FetchState.Uninitialized,
        pageInvalidations: [],
    },
    currentRequest: {
        expiryPage: false, pageNumber: 1,
        pageSize: 10, groups: [], eligibilityStatus: ExpenseEligibility.None
    },
    expenseAttachments: {},
    trasactionsToRemove: [],
    transactionRemoveCompleted: false,
    importedExpenses: [],
    importedExpensesFetchState: FetchState.Uninitialized
};

function updatePageData (expenseGroup: ExpenseGroup, response:
    { page: number, records: PossibleExpense[], numberOfPages: number }, statusChangeUpdate: boolean) {
    const originalPageContent = expenseGroup.records[response.page];
    const records = [ ...response.records ];

    if (originalPageContent && originalPageContent.length && statusChangeUpdate) {
        for (let index = 0; index < originalPageContent.length; index++) {
            if (records[index] !== undefined &&
                originalPageContent[index].eligibilityStatus !== ExpenseEligibility.None
                && originalPageContent[index].eligibilityStatus !== records[index].eligibilityStatus
            ) {
                records.splice(index, 0, originalPageContent[index]);
            }
        }
    }
    Vue.set(expenseGroup, "page", response.page);
    Vue.set(expenseGroup, "numberOfPages", response.numberOfPages);
    Vue.set(expenseGroup, "expenseFetchState", FetchState.Loaded);
    Vue.set(expenseGroup.records, `${response.page}`, records);
}

function getUndoableTransactions (expenseGroup: ExpenseGroup): number [] {
    const records: number [] = [];
    for (const page in expenseGroup.records) {
        expenseGroup.records[page].forEach(t => {
            if (t.eligibilityStatus !== ExpenseEligibility.None) {
                records.push(t.transactionId);
            }
        });
    }
    return records;
}

export const useExpenseV2Store = defineStore("expenseV2", {
    state: (): ExpenseStateNew => (_.cloneDeep(initialState)),
    getters: {
        expensesByPage: state =>
            (state.expenses.records && state.expenses.records[state.expenses.page]) ?
                state.expenses.records[state.expenses.page] : null,
        expensesFetchState: state => state.expenses.expenseFetchState,
        areExpensesLoading: state => state.expenses.expenseFetchState === FetchState.Loading,
        expensesPage: state => state.expenses.page,
        expensesPageCount: state => state.expenses.numberOfPages,
        likelyUndoableTransactions: state => {
            return getUndoableTransactions(state.expenses);
        },
        importedExpensesLoaded: state => state.importedExpensesFetchState === FetchState.Loaded
    },
    actions: {
        addExpenseAttachment (addParams: ExpenseAttachmentLinkAdd) {
            if (!this.expenseAttachments[addParams.expenseId]) {
                Vue.set(<any>this.expenseAttachments, addParams.expenseId, {});
            }

            Vue.set(this.expenseAttachments[addParams.expenseId], addParams.filename, addParams.link);
        },
        setExpensesFetchState (fetchState: FetchState) {
            this.expenses.expenseFetchState = FetchState.Loading;
        },
        setExpensesPage (page: number) {
            Vue.set(this.expenses, "page", page);
        },
        setExpenses (response: ExpenseGroup) {
            this.transactionRemoveCompleted = false;
            updatePageData(this.expenses, {
                    page: response.page,
                    records: <PossibleExpense[]><any>response.records,
                    numberOfPages: response.numberOfPages
                },
                this.trasactionsToRemove.length > 0);
        },
        setEligibilityMutation (response: ExpenseEligibilityStatus) {
            const updatedRecords = expenseHelper.updateEligibilityStatus(this.expenses, response.expenseStatusList);
            updatedRecords.forEach(record => {
                Vue.set(this.expenses.records, `${record.page}`, record.records);
            });
        },
        setExpense (params: ExpenseEditParams) {
            // jump out of setting an expense if the expense type is none;
            // the expenses set via the reimbursed tab do not trigger notifications, or need to be removed after edit
            if (params.expenseType === ExpenseEligibilityGroup.None) {
                return;
            }
            let expenseDictionary: ExpenseDictionary = null;
            expenseDictionary = this.expenses.records;

            for (const pageNumber in expenseDictionary) {
                for (const potentialExpense of expenseDictionary[pageNumber]) {
                    if (potentialExpense.transactionId === params.transactionId) {
                        potentialExpense.description = params.expense.name;
                        potentialExpense.eligibleAmount = params.expense.amount;
                        potentialExpense.expense = params.expense;
                        return;
                    }
                }
            }

            window.console.error(`expense.setExpense: could not find transaction with id: ${params.transactionId}`);
        },
        setExpenseEvidenceAttachment (params: ExpenseEvidenceUpdateParams) {
            expenseHelper.mapFilesToNames(this.expenses, params);
        },
        removeTransaction (record: TransactionToRemove) {
            const updatedRecords = expenseHelper.removeTransaction(this.expenses, record);
            updatedRecords.forEach(record => {
                Vue.set(this.expenses.records, `${record.page}`, record.records);
            });
        },
        addTransactionToDisappear (record: TransactionToRemove) {
            const index = this.trasactionsToRemove.findIndex(e => e.transactionId === record.transactionId);
            if (index > -1) {
                this.trasactionsToRemove.splice(index, 1);
            }
            this.trasactionsToRemove.push(record);
        },
        removeTransactionToDisappear (transactionId: number) {
            const index = this.trasactionsToRemove.findIndex(e => e.transactionId === transactionId);
            if (index > -1) {
                this.trasactionsToRemove.splice(index, 1);
                this.transactionRemoveCompleted = true;
            }
        },
        markPageAsInvalid (pageNumber: number) {
            this.expenses.pageInvalidations[pageNumber] = false;
        },
        markExpenseAsInvalid () {
            this.expenses = {
                records: {},
                page: 1,
                numberOfPages: 0,
                transactionType: null,
                expenseFetchState: FetchState.Uninitialized,
                pageInvalidations: []
            };
        },
        setImportedExpensesFetchState (fetchState: FetchState) {
            this.importedExpensesFetchState = fetchState;
        },
        setImportedExpenses (importedExpenses: ImportedExpense[]) {
            importedExpenses.forEach(x => {
                x.amountRemaining = x.amount;
                x.importedExpenseReimbursements.forEach(y => {
                    x.amountRemaining -= y.amount;
                });
            });
            this.importedExpenses = importedExpenses;
        },
        async loadExpenses (params: ExpenseRequest): Promise<any> {
            const pageFixed = params.pageNumber ? params.pageNumber : 1;
            const currentInvalidationForPage = this.expenses.pageInvalidations[pageFixed];
            const isLoaded = !params.expiryPage && currentInvalidationForPage === FetchState.Loaded;
            this.currentRequest = params;

            if (this.expenses.records && this.expenses.records[pageFixed]
                && isLoaded) {
                this.setExpensesPage(pageFixed);
                return Promise.resolve();
            }

            Vue.set(this.expenses.pageInvalidations, pageFixed, FetchState.Loading);

            return expenseService.getExpenses({
                    pageNumber: params.pageNumber,
                    pageSize: params.pageSize,
                    groups: params.groups,
                    eligibilityStatus: params.eligibilityStatus,
                    expiryPage: params.expiryPage,
                })
                .then(response => {
                    Vue.set(this.expenses.pageInvalidations, pageFixed, FetchState.Loaded);
                    this.setExpensesPage(pageFixed);
                    this.setExpenses(response.data);
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        updateExpense (params: ExpenseEditParams): Promise<any> {
            const expense = { ...params.expense };
            const isNewExpense = expense.id === 0 && params.transactionId === 0;

            const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
            const expenseReimbursedStore = useExpenseReimbursedStore();
            const expenseBalanceSummaryStore = useExpenseBalanceSummaryStore();
            return expenseService.editExpense({
                transactionId: params.transactionId,
                name: expense.name,
                amount: expense.amount,
                comment: expense.comment,
                recurringType: expense.recurringType,
                expenseCategory: expense.expenseCategory,
                eligibilityStatus: expense.eligibilityStatus,
                expensePaid: expense.expensePaid,
                dateOfService: expense.dateOfService,
                isPayToProvider: expense.isPayToProvider,
                providerName: expense.providerName,
                providerMemberNumber: expense.providerMemberNumber,
                providerAddress1: expense.providerAddress1,
                providerAddress2: expense.providerAddress2,
                providerCity: expense.providerCity,
                providerStateCode: expense.providerStateCode,
                providerZip: expense.providerZip,
                wexAccountType: expense.wexAccountType,
                wexExpenseCategory: expense.wexExpenseCategory,
                wexExpenseType: expense.wexExpenseType,
                numberOfMiles: expense.numberOfMiles,
                mileageDollarValue: expense.mileageDollarValue,
            }, expense.id)
                .then(response => {

                    params.expense = response.data.expense;
                    if (params.transactionId > 0) {
                        // We are editing an expense, so set it in the store
                        this.setExpense(params);
                    }
                    if (params.hasLinkedClaim) {
                        const expenseLinkRequest = {
                            expenseId: params.expense.id,
                            claimNumbers: params.expenseLinkRequest.claimNumbers,
                            comment: params.expenseLinkRequest.comment
                        };

                        const claimStore = useClaimStore();
                        claimStore.linkClaimsToExpense(expenseLinkRequest);
                    }
                    if (params.expense.eligibilityStatus === ExpenseEligibility.Eligible || params.expense.amount ===
                        0) {
                        const unreimbursedExpense: any = expense;
                        unreimbursedExpense.isSelected = false;
                        if (isNewExpense) {
                            expenseUnreimbursedStore.resetUnreimbursedState();
                            expenseUnreimbursedStore.load();
                        } else {
                            expenseUnreimbursedStore.updateUnreimbursedExpense(unreimbursedExpense);
                        }

                        if (params.expense.amount === 0) {
                            this.markExpenseAsInvalid();
                            this.loadExpenses();
                        }
                        expenseReimbursedStore.clearDictionary();
                        expenseReimbursedStore.reloadPage();
                    }
                    if (params.isEligibleAmountChanged || isNewExpense) {
                        const expenseData: { amount: number; expenseId: number } = {
                            amount: expense.amount,
                            expenseId: response.data.expense.id
                        };
                        expenseBalanceSummaryStore.updateReimburseMeAmount(expenseData as ExpenseReimburseMeParams);
                        expenseBalanceSummaryStore.retrieveBalanceSummary();
                    }

                    return response.data.expense.id;
                })
                .then(id => {
                    return this.addExpenseEvidence({
                        files: expense.expenseEvidenceAttachments,
                        expenseId: id,
                        transactionType: params.expenseType
                    }).then(response => {
                        return id;
                    });
                })
                .then(id => {
                    expenseUnreimbursedStore.reloadPage();
                    expenseReimbursedStore.updateExpense(expense);
                    return id;
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        addExpenseEvidence ({ files, expenseId, transactionType }) {
            if (files && files.length) {
                return expenseService.addExpenseEvidence(files, expenseId)
                    .then(response => {
                        const attachmentUpdate = {
                            expenseId,
                            transactionType,
                            expenseEvidenceNames: response.data,
                            isDelete: false
                        };
                        this.setExpenseEvidenceAttachment(attachmentUpdate);
                        return logEmployeeExpenseAttachmentUploaded();
                    });
            }
            return Promise.resolve();
        },
        afterEligibilityChange () {
            this.currentRequest.expiryPage = true;
            this.loadExpenses(this.currentRequest);
            let pageNumber = this.expenses.page + 1;
            while (pageNumber <= this.expenses.numberOfPages) {
                this.markPageAsInvalid(pageNumber);
                pageNumber++;
            }
        },
        setEligibility (expenseStatusUpdates: ExpenseEligibilityStatus) {
            const expenseBalanceSummaryStore = useExpenseBalanceSummaryStore();
            return expenseService.setEligibilityStatus(expenseStatusUpdates)
                .then(response => {
                    for (const expense of response.data.expenses) {
                        const params: ExpenseEditParams = {
                            transactionId: expense.transactionId,
                            expense: {
                                id: expense.expenseId,
                                name: expense.name,
                                amount: expense.amount,
                                reimbursedAmount: expense.reimbursedAmount,
                                comment: expense.comment,
                                expensePaid: expense.expensePaid,
                                recurringType: expense.recurringType,
                                expenseCategory: expense.expenseCategory,
                                attachmentNames: [],
                                eligibilityStatus: ExpenseEligibility.None,
                                expenseEvidenceAttachments: [],
                                dateOfService: expense.dateOfService,
                                transactionAmount: expense.transactionAmount,
                                isManualExpense: expense.isManualExpense
                            },
                            expenseType: ExpenseEligibilityGroup.None
                        };

                        this.setExpense(params);


                        const item = _.find(expenseStatusUpdates.expenseStatusList,
                            item => item.transactionId === expense.transactionId);
                        expenseBalanceSummaryStore.addReimburseMeAmount({
                            amount: expense.amount,
                            transactionId: expense.transactionId,
                            expenseId: expense.expenseId,
                            eligibility: item.eligibilityStatus
                        });
                    }

                    const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
                    const expenseReimbursedStore = useExpenseReimbursedStore();

                    expenseUnreimbursedStore.resetUnreimbursedState();
                    expenseUnreimbursedStore.load();
                    expenseReimbursedStore.clearDictionary();
                    expenseReimbursedStore.reloadPage();
                    expenseBalanceSummaryStore.retrieveBalanceSummary();
                    this.setEligibilityMutation(expenseStatusUpdates);

                })
                .then(resp => {
                    const transactions: TransactionToRemove[] = [];

                    expenseStatusUpdates.expenseStatusList.forEach(e => {
                        const record: TransactionToRemove = {
                            transactionType: expenseStatusUpdates.transactionType,
                            timerId: null,
                            transactionId: e.transactionId
                        };
                        transactions.push(record);
                        if (e.eligibilityStatus !== ExpenseEligibility.None) {
                            this.removeTransaction(record);
                            this.removeTransactionToDisappear(record.transactionId);
                            this.addTransactionToDisappear(record);
                        } else {
                            this.removeTransactionToDisappear(record.transactionId);
                        }
                    });

                    this.afterEligibilityChange();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        expenseAttachmentRemove (params: ExpenseAttachmentParams) {
            const attachmentName = params.attachmentName;
            const expense = params.expense;
            return expenseService.removeExpenseEvidence(expense.id, attachmentName)
                .then(response => {
                    if (!response.data) {
                        window.console.error(`Could not remove expense attachment ${attachmentName}` +
                            ` from expense ${expense.name} with id ${expense.id}`);
                        return;
                    }
                    const attachmentUpdate = {
                        expenseId: expense.id,
                        transactionType: params.transactionType,
                        expenseEvidenceNames: [ params.attachmentName ],
                        isDelete: true
                    };
                    this.setExpenseEvidenceAttachment(attachmentUpdate);
                    return expense.id;
                })
                .then(id => {
                    const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
                    const expenseReimbursedStore = useExpenseReimbursedStore();

                    expenseUnreimbursedStore.reloadPage();
                    expenseReimbursedStore.removeExpenseEvidence({ attachmentName, id });
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        expenseAttachmentGet (params: ExpenseAttachmentParams) {
            if (this.expenseAttachments[params.expense.id] &&
                this.expenseAttachments[params.expense.id][params.attachmentName]) {
                return;
            }

            const attachmentName = params.attachmentName;
            const expense = params.expense;
            const newTab = window.open();
            return expenseService.expenseEvidenceGetDownloadLink(expense.id, attachmentName).then(response => {
                    newTab.location.href = response.data;
                    setTimeout(() => {
                        if (newTab.location && newTab.location.href === "about:blank") {
                            newTab.close();
                        }
                    }, 2000);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    newTab.close();
                });
        },
        getImportedExpenses (): Promise<any> {
            // already loading ANYTHING then ditch out
            if (this.importedExpensesFetchState === FetchState.Loading) return Promise.resolve();

            this.setImportedExpensesFetchState(FetchState.Loading);

            return expenseService.getImportedExpenses()
                .then(response => {
                    this.setImportedExpensesFetchState(FetchState.Loaded);
                    this.setImportedExpenses(response.data);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        importedExpenseAttachmentGet (params) {
            const newTab = window.open();
            return expenseService.importedExpenseEvidenceGetDownloadLink(params.reimbursedExpenseId, params.fileName)
                .then(response => {
                    newTab.location.href = response.data;
                    setTimeout(() => {
                        if (newTab.location && newTab.location.href === "about:blank") {
                            newTab.close();
                        }
                    }, 2000);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    newTab.close();
                });
        },
        deleteExpense (expenseId: number) {
            const notifierStore = useNotifierStore();
            const claimStore = useClaimStore();
            const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
            const expenseReimbursedStore = useExpenseReimbursedStore();

            return expenseService.deleteExpense(expenseId).then(() => {
                    claimStore.removeLinksToExpense(expenseId).then(() => {
                        notifierStore.setSuccess("Expense deleted successfully.");
                        expenseUnreimbursedStore.reloadPage();
                        expenseReimbursedStore.clearDictionary();
                        expenseReimbursedStore.reloadPage();
                    });
                })
                .catch(() => {
                    notifierStore.setDanger("Unable to delete expense.");
                });
        },
        setIneligible (transactionId: number) {
            const eligibilityChangeRequest: ExpenseEligibilityStatus = {
                transactionType: ExpenseEligibilityGroup.None,
                expenseStatusList: [ { transactionId, eligibilityStatus: ExpenseEligibility.Ineligible } ]
            };
            const notifierStore = useNotifierStore();
            const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
            const expenseReimbursedStore = useExpenseReimbursedStore();

            this.setEligibility(eligibilityChangeRequest).then(() => {
                    notifierStore.setSuccess("Expense updated successfully.");
                    expenseUnreimbursedStore.reloadPage();
                    expenseReimbursedStore.clearDictionary();
                    expenseReimbursedStore.reloadPage();
                })
                .catch(() => {
                    notifierStore.setDanger("Unable to set ineligible status.");
                });
        }
    }
});